// ©2024 Austin App House. All rights reserved.
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { toggleSecondModalClose } from '../../storage/actions/modalsActions';
import CustomSelect from '../layout/CustomSelect';
import { dashboard as dashboardSelectStyle, dashboardSelectClassNames } from '../../utils/customSelectStyle';
import Notification from '../notification';

const ChangeAccountOwnerModal = ({ toggleSecondModalClose, modalState }) => {
  const type = modalState?.type || {};
  const users = type.users || [];
  const currentOwnerId = type.currentOwnerId;

  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (modalState?.isOpen) {
      setSelected(null);
      setSubmitting(false);
    }
  }, [modalState?.isOpen]);

  const options = useMemo(() => users
    .filter((u) => (u._id || u.id) !== currentOwnerId)
    .map((u) => ({
      value: u._id || u.id,
      label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
    })), [users, currentOwnerId]);

  const close = () => {
    if (submitting) return;
    toggleSecondModalClose('changeAccountOwnerModal', true);
  };

  const onSubmit = async () => {
    if (!selected) {
      Notification('warning', { message: 'Please select a new account owner.' });
      return;
    }
    setSubmitting(true);
    try {
      await type.onSubmit?.(selected.value);
      toggleSecondModalClose('changeAccountOwnerModal', true);
    } catch (error) {
      setSubmitting(false);
    }
  };

  if (!modalState?.isOpen) return null;

  return (
    <div
      className="modal_block dashboard-modal dashboard-modal--centered modal_import showed"
      role="presentation"
      onClick={close}
    >
      <div
        className="modal_container"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal_head">
          <div className="modal_title">Change Account Owner</div>
          <button type="button" className="close_btn" aria-label="close" onClick={close}>
            ×
          </button>
        </div>
        <div className="modal_body">
          <div className="modal_content">
            Select which company user should become the new account owner.
          </div>
          <div className="field_col">
            <label className="field_name" htmlFor="new-account-owner">New account owner</label>
            <div className="dashboard-select-wrap">
              <CustomSelect
                inputId="new-account-owner"
                styles={dashboardSelectStyle}
                classNames={dashboardSelectClassNames}
                options={options}
                value={selected}
                onChange={setSelected}
                placeholder={options.length ? 'Select a user' : 'No other users available'}
                isDisabled={!options.length}
                isSearchable={false}
              />
            </div>
          </div>
        </div>
        <div className="modal_footer">
          <button
            type="button"
            className="dashboard-btn dashboard-btn--secondary"
            onClick={close}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="dashboard-btn dashboard-btn--primary"
            onClick={onSubmit}
            disabled={submitting || !options.length}
          >
            {submitting ? 'Saving…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  modalState: state.modalState.changeAccountOwnerModal,
});

export default connect(mapStateToProps, { toggleSecondModalClose })(ChangeAccountOwnerModal);
